/**
 * <s-usd-price> — Shows approximate USD prices to visitors outside India.
 *
 * Wraps a price block. Each amount inside carries `data-inr-cents` (INR in
 * paise). When the visitor is outside India, every amount is replaced with a
 * rounded-up whole-dollar USD figure and `[data-usd-note]` is revealed.
 * Checkout is untouched; buyers always pay in INR.
 *
 * Element dataset:
 *   data-rate-url        — Rate endpoint returning { rates: { USD } } for base INR.
 *   data-fallback-rate   — USD per 1 INR used when the rate endpoint fails.
 *   data-suggestions-url — Shopify browsing-context endpoint used to detect country.
 *
 * The rate is cached in localStorage per day and the country per session, so
 * one network call of each runs per visitor rather than per price.
 */

import { getVisitorCountry, getPreferredCurrency, CURRENCY_EVENT } from '@theme/visitor-country';

const RATE_KEY = 'solei-usd-rate';

let ratePromise;

function today() {
  return new Date().toISOString().slice(0, 10);
}

function readJSON(storage, key) {
  try {
    return JSON.parse(storage.getItem(key) || 'null');
  } catch {
    return null;
  }
}

function writeJSON(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable (private mode); fine to skip caching */
  }
}

function getRate(rateUrl, fallbackRate) {
  if (ratePromise) return ratePromise;
  const cached = readJSON(localStorage, RATE_KEY);
  if (cached && cached.date === today() && cached.rate > 0) {
    ratePromise = Promise.resolve(cached.rate);
    return ratePromise;
  }
  ratePromise = fetch(rateUrl)
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error('rate failed'))))
    .then((data) => {
      const rate = Number(data?.rates?.USD);
      if (!(rate > 0)) throw new Error('bad rate');
      writeJSON(localStorage, RATE_KEY, { date: today(), rate });
      return rate;
    })
    .catch(() => (fallbackRate > 0 ? fallbackRate : null));
  return ratePromise;
}

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

class UsdPrice extends HTMLElement {
  #onCurrencyChange = (event) => this.#render(event.detail?.currency);

  connectedCallback() {
    window.addEventListener(CURRENCY_EVENT, this.#onCurrencyChange);
    this.#apply();
  }

  disconnectedCallback() {
    window.removeEventListener(CURRENCY_EVENT, this.#onCurrencyChange);
  }

  async #apply() {
    const country = await getVisitorCountry(this.dataset.suggestionsUrl);
    if (country === 'IN') return;

    const rate = await getRate(this.dataset.rateUrl, Number(this.dataset.fallbackRate));
    if (!rate || !this.isConnected) return;

    this.rate = rate;
    this.#render(getPreferredCurrency());
  }

  /** Swaps every amount between rounded-up USD and the original INR text. */
  #render(currency) {
    if (!this.rate) return;
    const showUsd = currency !== 'INR';

    this.querySelectorAll('[data-inr-cents]').forEach((amount) => {
      if (amount.dataset.inrText === undefined) amount.dataset.inrText = amount.textContent;
      if (showUsd) {
        const inr = Number(amount.dataset.inrCents) / 100;
        amount.textContent = `${usdFormatter.format(Math.ceil(inr * this.rate))} USD`;
      } else {
        amount.textContent = amount.dataset.inrText;
      }
    });
    this.querySelectorAll('[data-usd-note]').forEach((note) => {
      note.hidden = !showUsd;
    });
  }
}

if (!customElements.get('s-usd-price')) {
  customElements.define('s-usd-price', UsdPrice);
}
